<template>
<!-- eslint-disable max-len -->
<div style="position: relative; height: 100%; perspective: 1px;">
<div
  v-if="!autoSubmit"
  class="reason-bubble"
  :class="{ right, native, sf: (starfighter) }"
  :style="{
    'max-height': (decision.reasonBubbleMaxHeightOverride ? decision.reasonBubbleMaxHeightOverride : undefined),
  }"
>
  <div
    v-if="!choiceOrFreeResponse && selectedChoices && !['form'].includes(decision.type)"
    class="choice btn-white"
    :class="{ native }"
  >
    <h4 v-if="!['roleAllocation', 'projectAllocation'].includes(decision.type)">Selected Choices:</h4>
    <h4 v-else>Selected Allocations:</h4>
    <div style="overflow-y: auto; overflow-x: hidden;" :style="choicesContainerStyle">
      <h3 v-if="selectedChoices.length === 0">None</h3>
      <accordion
        v-for="(choice, i) in selectedChoices"
        :key="`choice-${i}`"
        :accordion-id="`choice-${i}`"
      >
        <template v-if="decision.type === 'sliders'">
          <div class="choice-header" slot="button" style="height: 50px;">
            <!--
              Never use v-html with end-user generated content, because it's a vector for XSS attacks
              This is what the following line used to be:
              <h3>{{ choice.title }}</h3>
            -->
            <h3 v-html="getChoiceDisplayTitle(choice)" style="margin: 0;"></h3>
            <span v-if="!(choice && choice.sliderHideValue)" class="choice-cost">
              Selected {{ choice.valueString }}
            </span>
          </div>
        </template>
        <template v-else-if="decision.type === 'matching'">
          <div
            class="choice-header"
            slot="button"
            style="
              height: fit-content;
              width: 100%;
              padding: 8px 4px;
              font-size: 26px;
              line-height: 1.13;
              font-weight: 500;
              display: flex;
              justify-content: space-between;
            "
          >
            <!--
              Never use v-html with end-user generated content, because it's a vector for XSS attacks
              This is what the following line used to be:
              <h3>{{ choice.title }}</h3>
            -->
            <div v-html="getChoiceDisplayTitle(choice)"></div>
            <div v-if="choice && choice.category && (choice.category.displayName || choice.category.name)">
              <span style="font-size: 80%;">Match:</span> <span v-html="(choice.category.displayName || '').replaceAll(/font-size:[^;]*;/g, '') || choice.category.name"></span>
            </div>
          </div>
        </template>
        <template v-else-if="decision.type === 'scale'">
          <div
            class="choice-header"
            slot="button"
            style="
              height: fit-content;
              width: 100%;
              padding: 8px 4px;
              font-size: 26px;
              line-height: 1.13;
              font-weight: 500;
              display: flex;
              justify-content: space-between;
            "
          >
            <div v-html="getChoiceDisplayTitle(choice)"></div>
            <div v-if="choice && choice.category && (choice.category.displayName || choice.category.name)">
              <span style="font-size: 80%;">Selected:</span> <span v-html="(choice.category.displayName || '').replaceAll(/font-size:[^;]*;/g, '') || choice.category.name"></span>
            </div>
          </div>
          <p
            v-if="choice && choice.description"
            v-html="getChoiceDisplayDescription(choice)"
            class="choice-description"
            slot="body"
            :style="choiceDescriptionStyle"
          ></p>
        </template>
        <template v-else-if="decision.type === 'matrix'">
          <div
            class="choice-header"
            slot="button"
            style="
              height: fit-content;
              width: 100%;
              padding: 8px 4px;
              font-size: 26px;
              line-height: 1.13;
              font-weight: 500;
              display: flex;
              justify-content: space-between;
            "
          >
            <div v-html="getChoiceDisplayTitle(choice)"></div>
            <div v-if="choice && choice.categories && choice.categories.length">
              <span style="font-size: 80%;">Selected:</span>
              <span
                v-for="(category, c) in choice.categories"
                :key="`catlist-${c}`"
                v-if="category && (category.displayName || category.name)"
                v-html="
                  `${
                    (category.displayName || '').replaceAll(/font-size:[^;]*;/g, '') || category.name
                  }${
                    (c < choice.categories.length - 1) ? '; ' : ''
                  }`
                "
              ></span>
            </div>
          </div>
          <p
            v-if="choice && choice.description"
            v-html="getChoiceDisplayDescription(choice)"
            class="choice-description"
            slot="body"
            :style="choiceDescriptionStyle"
          ></p>
        </template>
        <div v-else-if="['roleAllocation', 'projectAllocation'].includes(decision.type)" class="choice-header" slot="button" style="height: 50px;">
          <h3 v-if="decision.type === 'roleAllocation'" style="margin: 0;">{{roles[choice.allocateToRole].title}}</h3>
          <h3 v-else style="margin: 0;">{{getChoiceDisplayTitle(choice)}}</h3>
          <span class="choice-cost">
            <span class="currency">{{ choice.allocation || 0 | currency }}</span>
          </span>
        </div>
        <div v-else class="choice-header" slot="button" style="height: 50px;">
          <!--
            Never use v-html with end-user generated content, because it's a vector for XSS attacks
            This is what the following line used to be:
            <h3>{{ choice.title }}</h3>
          -->
          <h3 v-html="getChoiceDisplayTitle(choice)" style="margin: 0;"></h3>
          <span
            v-if="choice && !(choice.hideCost || forceHideCost) && !isNaN(parseFloat(choice.displayedCost || choice.cashAdjustment || 0))"
            class="choice-cost"
          >
            COST <span class="currency">{{ -parseFloat((choice.displayedCost != undefined) ? choice.displayedCost : choice.cashAdjustment) || 0 | currency }}</span>
          </span>
        </div>
        <!--
          Never use v-html with end-user generated content, because it's a vector for XSS attacks,
          but this is okay here since choice.description is only used/set internally.
          If we decide we don't really need full html capability in descriptions, but still want proper new lines,
          replace the following line with:
          <p class="choice-description">{{choice.description}}</p>
        -->
        <p
          v-if="choice && choice.description"
          v-html="getChoiceDisplayDescription(choice)"
          class="choice-description"
          slot="body"
          :style="choiceDescriptionStyle"
        ></p>
      </accordion>
    </div>
  </div>
  <div v-else-if="choiceOrFreeResponse" class="choice" :class="{ native }">
    <div
      v-if="!(choiceImage(choiceOrFreeResponse)) || (choiceImage(choiceOrFreeResponse) && choiceOrFreeResponse.showTitle)"
      class="choice-header"
      :style="!(choiceOrFreeResponse.description) ? 'justify-content: center;' : ''"
    >
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks
        This is what the following line used to be:
        <h2>{{ choice.title }}</h2>
      -->
      <h2 v-if="!isFreeResponse || !decision.hideTitle" v-html="choiceOrFreeResponseDisplayTitle"></h2>
      <p v-if="showCost && !(choiceOrFreeResponse.hideCost || forceHideCost) && !isNaN(parseFloat(choice.displayedCost || choice.cashAdjustment || 0))" class="choice-cost">
        COST <span class="currency">{{ -parseFloat((choice.displayedCost != undefined) ? choice.displayedCost : choice.cashAdjustment) || 0 | currency }}</span>
      </p>
    </div>
    <div style="display: flex; flex-flow: row; justify-content: center; max-height: 90%;">
      <img
        v-if="choiceImage(choiceOrFreeResponse)"
        :src="choiceImage(choiceOrFreeResponse)"
        style="max-width: 250px; max-height: 240px; object-fit: contain; margin-right: 15px;"/>
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks,
        but this is okay here since choice.description is only used/set internally.
        If we decide we don't really need full html capability in descriptions, but still want proper new lines,
        replace the following line with:
        <p class="choice-description">{{choice.description}}</p>
      -->
      <p v-html="choiceOrFreeResponseDisplayDescription" class="choice-description" :style="choiceDescriptionStyle"></p>
    </div>
  </div>

  <form class="reason-form" :class="{ 'allocation-reason': choices.length > 0, native: (native) }">
    <div v-if="showReasoningForm" class="form-group">
      <label for="reason">
        <div
          :style="{
            width: '100%',
            display: 'flex',
            'flex-direction': 'row',
            'justify-content': 'space-between',
            'align-items': 'flex-end',
            'font-size': (isFreeResponse ? '22px' : '32px'),
          }"
        >
          <div v-html="prompt">
          </div>
          <div v-if="reasoningRequired" style="font-size: 70%; text-align: right;">
            (At least {{reasoningMinimumLength}} characters required)
          </div>
        </div>
      </label>
      <textarea name="reason" id="reason" class="form-control" v-model="reason" v-focus :tabindex="1" :maxlength="reasoningMaximumLength" @input="(ev) => checkReasonRequirementFulfilled()"></textarea>
      <div class="character-counter">{{reason.length}} / {{reasoningMaximumLength}}</div>
    </div>

    <div v-if="!(native)" class="buttons">
      <button v-if="!isFreeResponse" class="btn" id="back" @click.prevent="onClickBack" :tabindex="2" :class="{ 'back-native': (native) }">
        <span v-html="backButtonText === '' ? 'Back' : backButtonText"></span>
      </button>
      <button
        type="submit"
        class="btn"
        id="submit"
        @click.prevent="onClickApprove"
        :tabindex="3"
        :class="{ 'submit-native': (native) }"
        :disabled="!reasonRequirementFulfilled || disableSubmit || waitingForIFrames"
      >
        <span v-html="submitButtonText === '' ? 'Reply' : submitButtonText"></span>
      </button>
    </div>
  </form>
  <tooltip
    target="reasonAndConfirmChoiceBubble"
  >
  </tooltip>
</div>
</div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { incrementVersionCheckerProtectionCount, decrementVersionCheckerProtectionCount } from '../../../utils/version';

import Accordion from '../../ui/display/Accordion';
import Tooltip from './Tooltip';
import { handleIFrames } from '../../../utils/utils';

export default {
  props: {
    decision: { type: [Object, Boolean] },
    choice: { type: Object },
    selectedChoices: { type: Array, default: () => [] },
    value: { type: String },
    choices: { type: Array, default: () => [] },
    showCost: { type: Boolean },
    // tail points right
    right: { type: Boolean },
    disableReasoningForm: { type: Boolean, default: false },
    preventReasoningInputOverride: { type: Boolean, default: false },
    decisionReasoningMinimumLength: { type: Number, default: 0 },
    decisionReasoningMinimumLengthOverride: { type: Boolean, default: false },
    decisionReasoningMaximumLength: { type: Number, default: 0 },
    decisionReasoningMaximumLengthOverride: { type: Boolean, default: false },
    noWaitForIFrames: { type: Boolean, default: false },
    disableSubmit: { type: Boolean, default: false },
    submitButtonText: { type: String, default: 'Reply' },
    submitButtonSound: { type: String },
    backButtonText: { type: String, default: 'Back' },
  },
  components: {
    Accordion,
    Tooltip,
  },

  created() {
    if (this.autoSubmit) {
      this.$emit('input', this.reason);
    }
    if (this.showReasoningForm) {
      this.setDisableForcedLandscape(true);
    }
  },
  async mounted() {
    incrementVersionCheckerProtectionCount();
    await Vue.nextTick();
    this.stopSounds();
    if (this.autoSubmit) {
      this.$emit('input', this.reason);
    } else if (!(this.decision.type) || ['choice', 'survey', 'freeResponse'].includes(this.decision.type)) {
      if (this.isFreeResponse && this.decision.descriptionVoiceLine) {
        await Vue.nextTick();
        this.playVoice({ line: this.decision.descriptionVoiceLine, queue: false });
      } else if (!(this.isFreeResponse) && this.choice.descriptionVoiceLine) {
        await Vue.nextTick();
        this.playVoice({ line: this.choice.descriptionVoiceLine, queue: false });
      }
    }
    await Vue.nextTick();
    handleIFrames(this);
  },
  destroyed() {
    decrementVersionCheckerProtectionCount();

    this.setDisableForcedLandscape(false);
  },

  data() {
    return {
      framesToLoad: 0,
      reason: this.value,
      previousReasonRequirementFulfilled: null
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'activeClass',
      'game',
      'selectedAvatar',
      'roles',
      'interpolateRegex',
      'replacements',
      'starfighter',
      'native'
    ]),
    waitingForIFrames() {
      return !(this.noWaitForIFrames || this.decision.noWaitForIFrames) && this.framesToLoad > 0;
    },
    choiceDisplayType() {
      if (this.decision.type !== 'choice') {
        return 'list';
      }
      if (!(this.decision.preventChoiceDisplayTypeOverride)
        && this.activeClass && this.activeClass.choiceDisplayTypeOverride
        && this.activeClass.choiceDisplayTypeOverride !== 'Do Not Force') {
        switch (this.activeClass.choiceDisplayTypeOverride) {
          case 'List': return 'list';
          case 'Carousel': return 'carousel';
          default: return 'list';
        }
      }
      if (!(this.decision.choiceDisplayType)) return 'list';
      return this.decision.choiceDisplayType;
    },
    autoSubmit() {
      return !(this.isFreeResponse)
        && !(this.decision.forceConfirmation)
        && (this.decision.forceSkipConfirmation
          || (!(this.showReasoningForm) && this.noMoreInfoToDisplay));
    },
    noMoreInfoToDisplay() {
      // no description text or carousel where description text was already displayed
      // TODO: should this logic include choice image/video?
      return !(this.anyChoiceHasDescriptionText)
        || ((!(this.decision.type) || ['choice'].includes(this.decision.type))
          && ['carousel'].includes(this.choiceDisplayType));
    },
    anyChoiceHasDescriptionText() {
      return this.decision && this.decision.choices && this.decision.choices.some(c => c.description);
    },
    choiceHasDescriptionText() {
      return (this.choice && this.choice.description)
        || (this.choices && this.choices.some(c => c.description));
    },
    isFreeResponse() {
      return this.decision && this.decision.type && this.decision.type === 'freeResponse';
    },
    choiceOrFreeResponse() {
      if (this.choice == null && this.isFreeResponse) {
        return {
          title: this.decision.title,
          description: this.decision.description,
          image: this.decision.image,
          hideCost: true,
          showTitle: false,
        };
      }
      return this.choice;
    },
    choiceOrFreeResponseDisplayTitle() {
      if (this.choiceOrFreeResponse && this.choiceOrFreeResponse.displayNameForConfirmation) {
        return this.choiceOrFreeResponse.displayNameForConfirmation
          .replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
      } else if (this.choiceOrFreeResponse && this.choiceOrFreeResponse.title) {
        return this.choiceOrFreeResponse.title
          .replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
      }
      return '';
    },
    choiceOrFreeResponseDisplayDescription() {
      if (this.choiceOrFreeResponse && this.choiceOrFreeResponse.description) {
        return this.choiceOrFreeResponse.description
          .replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
      }
      return '';
    },
    choiceDisplayTitle() {
      if (this.choice && this.choice.displayNameForConfirmation) {
        return this.choice.displayNameForConfirmation
          .replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
      } else if (this.choice && this.choice.title) {
        return this.choice.title
          .replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
      }
      return '';
    },
    choiceDisplayDescription() {
      if (this.choice && this.choice.description) {
        return this.choice.description
          .replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
      }
      return '';
    },
    prompt() {
      return this.isFreeResponse
        ? ((this.decision && this.decision.prompt)
          ? this.decision.prompt
          : 'Enter your answer:')
        : 'Explain your reasoning...';
    },
    reasoningInputOverride() {
      if (this.isFreeResponse) return 'Force On';
      if (this.preventReasoningInputOverride) return 'Do Not Force';
      return (this.activeClass != null) ? this.activeClass.reasoningInputOverride :
        ((this.activeEnvironment != null && this.activeEnvironment.name != null)
          ? this.activeEnvironment.reasoningInputOverride : 'Do Not Force');
    },
    showReasoningForm() {
      if (this.reasoningInputOverride === 'Force On') {
        return true;
      } else if (this.reasoningInputOverride === 'Force Off') {
        return false;
      }
      return !(this.disableReasoningForm);
    },
    reasoningMaximumLength() {
      if (this.decisionReasoningMaximumLengthOverride) {
        return this.decisionReasoningMaximumLength || 0;
      }
      if (this.activeClass && this.activeClass.reasoningMaximumLength) {
        return this.activeClass.reasoningMaximumLength;
      }
      if (this.game && this.game.reasoningMaximumLength) {
        return this.game.reasoningMaximumLength;
      }
      if (this.decisionReasoningMaximumLength) {
        return this.decisionReasoningMaximumLength;
      }
      return 256;
    },
    reasoningMinimumLength() {
      if (this.decisionReasoningMinimumLengthOverride) {
        return this.decisionReasoningMinimumLength || 0;
      }
      if (this.activeClass && this.activeClass.reasoningMinimumLength) {
        return this.activeClass.reasoningMinimumLength;
      }
      if (this.game && this.game.reasoningMinimumLength) {
        return this.game.reasoningMinimumLength;
      }
      if (this.decisionReasoningMinimumLength) {
        return this.decisionReasoningMinimumLength;
      }
      if (this.isFreeResponse) {
        return 0; // change if free response should have a different default
      }
      return 0;
    },
    reasoningRequired() {
      return (this.showReasoningForm && this.reasoningMinimumLength !== 0);
    },
    reasonRequirementFulfilled() {
      return !(this.reasoningRequired && this.reason.length < this.reasoningMinimumLength);
    },
    forceHideCost() {
      return (this.decision != null && ['sliders', 'matching'].includes(this.decision.type));
    },
    choiceDescriptionStyle() {
      if (this.selectedChoices && this.selectedChoices.length) {
        return {
          'overflow-y': 'hidden',
          'max-height': '1800px'
        };
      }
      const { reasonBubbleMaxHeightOverride } = this.decision;
      return {
        'max-height':
          ((this.showReasoningForm)
            ? undefined
            : (reasonBubbleMaxHeightOverride
              ? `calc(420px + (${reasonBubbleMaxHeightOverride} - 700px))`
              : '420px')),
      };
    },
    choicesContainerStyle() {
      const baseMaxHeight = ((this.showReasoningForm) ? '220px' : '420px');
      const { reasonBubbleMaxHeightOverride } = this.decision;
      return {
        'max-height':
          (reasonBubbleMaxHeightOverride
            ? `calc(${baseMaxHeight} + (${reasonBubbleMaxHeightOverride} - 700px))`
            : baseMaxHeight),
      };
    }
  },
  methods: {
    ...mapMutations([
      'setDisableForcedLandscape',
    ]),
    ...mapActions([
      'playSound',
      'playVoice',
      'stopSounds',
    ]),
    async playBackClick() {
      await Vue.nextTick();
      this.playSound({
        sound:
          this.game.backButtonSound
          || this.game.clickSoundOverride
          || this.resolveAssetPath('click_cut.mp3', 'ui/customer'),
        soundIsUnstoppable: true,
      });
    },
    async playSubmitClick() {
      await Vue.nextTick();
      this.playSound({
        sound: this.submitButtonSound || this.game.clickSoundOverride || this.resolveAssetPath('click_cut.mp3', 'ui/customer'),
        soundIsUnstoppable: true,
      });
    },
    async onClickBack() {
      await this.playBackClick();
      await new Promise(resolve => setTimeout(resolve, 250));
      this.$emit('back', this.reason);
    },
    async onClickApprove() {
      await this.playSubmitClick();
      await new Promise(resolve => setTimeout(resolve, 250));
      this.$emit('input', this.reason);
    },
    getChoiceDisplayTitle(choice) {
      if (choice && choice.displayNameForConfirmation) {
        return choice.displayNameForConfirmation
          .replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
      } else if (choice && choice.title) {
        return choice.title
          .replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
      }
      return '';
    },
    getChoiceDisplayDescription(choice) {
      if (choice && choice.description) {
        return choice.description
          .replace(this.interpolateRegex, (__, tag) => this.replacements[tag] || '');
      }
      return '';
    },
    choiceImage(choice) {
      if (choice.playerVariantImage) {
        const variant =
          this.selectedAvatar.imageVariants.find(v => v.name === choice.playerVariantImage);
        if (variant) {
          return this.resolveAssetPath(variant.image, 'characters');
        }
      }

      if (choice.image) {
        return this.resolveAssetPath(choice.image, 'images');
      }

      return null;
    },
    checkReasonRequirementFulfilled() {
      if (this.previousReasonRequirementFulfilled == null
        || this.previousReasonRequirementFulfilled !== this.reasonRequirementFulfilled) {
        this.$emit('reason-requirement-fulfilled-changed', this.reasonRequirementFulfilled);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.reason-bubble {
  position: absolute;
  width: 900px;
  min-height: 275px;
  max-height: 700px;
  padding: 15px 25px;
  display: flex;
  flex-flow: column;
}
.reason-bubble.sf {
  background-color: white;
  border: 4px solid black;
  border-radius: 24px;
}
.reason-bubble.native {
  min-height: 200px;
  height: unset;
  max-height: 620px;
  width: 1400px;
  left: -375px;
  padding: 0;
  border: 25px solid rgba(0, 0, 0, 0);
  border-image: url('/static/ui/customer/B2_TextBox_90.png') 90 fill;
  transform: translateZ(0);
}
.reason-bubble.sf::before {
  position: absolute;
  content: '';
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 50px 70px 0 0;
  border-color: transparent black transparent transparent;
  top: 200px;
  left: -70px;
  right: unset;
}
.reason-bubble.sf::after {
  position: absolute;
  content: '';
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 46px 64.4px 0 0;
  border-color: transparent white transparent transparent;
  top: 201px;
  left: -60px;
  right: unset;
}
.reason-bubble.right.sf::before {
  border-width: 50px 0 0 70px;
  border-color: transparent transparent transparent black;
  right: -70px;
}
.reason-bubble.right.sf::after {
  border-width: 46px 0 0 64.4px;
  border-color: transparent transparent transparent white;
  right: -60px;
}
.choice {
  position: relative;
  padding: 10px 25px;
  // reason-form has height of its content; choice fills the height available above
  min-height: 0%;
  display: flex;
  flex-flow: column;
  flex-shrink: 0;
}
.choice.native {
  margin-left: 580px;
}
.choice-header {
  height: 70px;
  display: flex;
  flex-flow: row;
  align-items: center;
  flex-shrink: 0;
}
.choice h2 {
  font-size: 39px;
  // font-weight: bold;
  margin: 0;
}
.choice-description {
  font-size: 24px;
  line-height: 40px;
  overflow-y: auto;
  margin-bottom: 5px;
  text-align: left;
  white-space: pre-line;
  max-height: 220px;
}
.choice-cost {
  position: absolute;
  right: 20px;
  margin: 0;
  font-size: 20px;
}
.choice-cost span {
  font-size: 32px;
  // font-weight: bold;
}
.reason-form {
  background-color: white;
  position: relative;
  // reason-form has height of its content; choice fills the height available above
  flex: 0 0 content;
  display: flex;
  flex-flow: column;
}
.reason-form.native {
  background-color: rgba(0, 0, 0, 0);
  padding: 0;
  margin-top: 10px;
  margin-left: 580px;
}
.reason-form .form-group {
  flex: 1 1 auto;
  display: flex;
  flex-flow: column;
}
.reason-form .buttons {
  flex: 0 1 70px;
  padding: 0px;
  margin-top: auto;
}
.reason-form label {
  font-size: 32px;
  // font-weight: bold;
  margin-left: 10px;
  margin-bottom: 5px;
  flex: 0 1 auto;
}
.reason-form textarea {
  font-size: 24px;
  line-height: 35px;
  min-height: 185px;
  max-height: 185px;
  flex: 1 1 auto;
}
.reason-form textarea:focus {
  border: 4px solid var(--primaryColor);
}
.character-counter {
  font-size: 85%;
  color: rgb(150, 150, 150);
  flex: 0 1 auto;
}
.reason-form button {
  width: 250px;
  height: 80px;
  // padding: 32px 0;
  font-size: 30px;
}
.reason-form.allocation-reason {
  height: 455px;
}
.reason-form.allocation-reason textarea {
  height: 255px;
}
.budgets-bar {
  margin: 10px -3px 5px;
}
#back {
  float: left;
}
#submit {
  float: right;
}
</style>



// WEBPACK FOOTER //
// src/components/game/ui/ReasonBubble.vue